// SourcePanel.jsx — the side-by-side source view, at page level (§6,
// Decision #13). The selected field's source page is displayed next to the
// form with prev/next and zoom; for .docx/pasted text the panel shows the
// source text scrolled to the selected item instead. Region highlighting is
// deliberately out — per-region bounding boxes were tried on the OCR tool
// and reverted (migrations 021/022); page-level sync gets most of the
// review-speed win without re-buying that lesson.
//
// The parent owns loading: fresh sessions reuse the in-memory page images
// from extraction; resumed sessions re-render the stored original on demand
// (loadStoredSource). This component just asks via onLoad() when it has
// nothing to show.

import { useEffect, useMemo, useRef, useState } from 'react';
import { T } from '../../theme';

const ZOOMS = [1, 1.5, 2];

/**
 * @param sourceDoc  { status: 'idle'|'loading'|'ready'|'error',
 *                     kind: 'pdf'|'image'|'docx'|'text',
 *                     pages?: [dataUrl…], text?: string, error?: string }
 * @param onLoad     ask the parent to load the stored original
 * @param focus      { page?: number, label?: string, seq: number } — set by
 *                   jump-to-source; seq forces re-focus on repeat jumps
 */
export default function SourcePanel({ sourceDoc, onLoad, focus }) {
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const textBoxRef = useRef(null);

  const { status, kind, pages, text, error } = sourceDoc || { status: 'idle' };
  const pageCount = pages?.length || 0;

  useEffect(() => {
    if (status === 'idle') onLoad?.();
  }, [status, onLoad]);

  // Jump-to-source: land on the field's page (images) or scroll its label
  // into view (text sources).
  useEffect(() => {
    if (!focus) return;
    if (focus.page && pageCount) setPage(Math.min(Math.max(focus.page, 1), pageCount));
  }, [focus, pageCount]);

  // For text sources, highlight + scroll to the focused label's first match.
  const textParts = useMemo(() => {
    if (text == null) return null;
    const label = focus?.label?.trim();
    if (!label) return [text];
    const at = text.toLowerCase().indexOf(label.toLowerCase());
    if (at === -1) return [text];
    return [text.slice(0, at), text.slice(at, at + label.length), text.slice(at + label.length)];
  }, [text, focus]);

  useEffect(() => {
    if (!textBoxRef.current) return;
    const mark = textBoxRef.current.querySelector('mark');
    if (mark) mark.scrollIntoView({ block: 'center' });
  }, [textParts]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div style={{ padding: 24, textAlign: 'center', font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted }}>
        <i className="fa-solid fa-spinner fa-spin" /> Loading the original document…
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div style={{ padding: 16, font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted }}>
        <i className="fa-solid fa-triangle-exclamation" style={{ color: T.orangeText }} />{' '}
        {error || 'The original document isn’t available — it may not have been stored when this session was created.'}
      </div>
    );
  }

  // Text-shaped sources (.docx / pasted text): the text, scrolled to the item.
  if (kind === 'docx' || kind === 'text') {
    return (
      <div
        ref={textBoxRef}
        style={{
          maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', margin: 10,
          background: T.bg, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusBtn,
          padding: 12, font: `400 12px/19px ${T.fontData}`, color: T.ink,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}
      >
        {textParts?.length === 3
          ? <>{textParts[0]}<mark style={{ background: T.badge.orange, padding: '1px 0' }}>{textParts[1]}</mark>{textParts[2]}</>
          : text}
      </div>
    );
  }

  // Page-image sources (pdf / image): pager + zoom + the page itself.
  const src = pages?.[page - 1];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: `1px solid ${T.borderSubtle}` }}>
        <button className="fmx-btn-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ opacity: page <= 1 ? 0.4 : 1 }}>
          <i className="fa-solid fa-chevron-left" />
        </button>
        <span style={{ font: `500 12px/17px ${T.fontSans}`, color: T.inkMuted }}>
          Page {page} of {pageCount}
        </span>
        <button className="fmx-btn-xs" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)} style={{ opacity: page >= pageCount ? 0.4 : 1 }}>
          <i className="fa-solid fa-chevron-right" />
        </button>
        <span style={{ flex: 1 }} />
        <button className="fmx-btn-xs" onClick={() => setZoom(z => ZOOMS[(ZOOMS.indexOf(z) + 1) % ZOOMS.length])}>
          <i className="fa-solid fa-magnifying-glass" /> {Math.round(zoom * 100)}%
        </button>
      </div>
      <div style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto', background: T.bgMuted, padding: 8 }}>
        {src
          ? <img src={src} alt={`Source page ${page}`} style={{ width: `${zoom * 100}%`, display: 'block', boxShadow: T.shadowSm, background: T.white }} />
          : <div style={{ padding: 20, font: `400 12px/17px ${T.fontSans}`, color: T.inkSoft }}>This page could not be rendered.</div>}
      </div>
    </div>
  );
}
